import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import bellIcon from '../assets/img/notificacionesWhite.png';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const NotificationBell = ({ notificationCount, userId, token, updateNotificationCount, theme }) => {
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const notificationBellRef = useRef(null);
  const isDarkMode = theme === 'dark';

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/notifications/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    }
  }, [userId, token]);

  useEffect(() => {
    if (notificationCount > 0 && !showNotifications) fetchNotifications();
  }, [notificationCount, showNotifications, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationBellRef.current && !notificationBellRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBellClick = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) fetchNotifications();
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Borra solo esta notificación
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/user/notifications/single/${notification._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => prev.filter(n => n._id !== notification._id));
      updateNotificationCount(-1);
      setShowNotifications(false);
      // Navega al contenido
      navigate(`/content/${notification.contentId}`);
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/user/notifications/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications([]);
      updateNotificationCount(-notificationCount);
      setShowNotifications(false);
    } catch (err) {
      console.error('Error clearing notifications:', err);
    }
  };

  const typeIcon = { recommend: '👍', share: '🔗', download: '⬇️' };
  const typeLabel = { recommend: 'recomendó', share: 'compartió', download: 'descargó' };

  return (
    <Container ref={notificationBellRef}>
      {notificationCount > 0 && (
        <Badge>{notificationCount > 99 ? '99+' : notificationCount}</Badge>
      )}
      <BellIcon
        src={bellIcon}
        alt="Notificaciones"
        onClick={handleBellClick}
        hasNew={notificationCount > 0}
      />

      {showNotifications && (
        <Dropdown isDark={isDarkMode}>
          <DropdownHeader isDark={isDarkMode}>
            <HeaderTitle isDark={isDarkMode}>Notificaciones</HeaderTitle>
            {notifications.length > 0 && (
              <ClearBtn onClick={handleClearAll}>Limpiar todo</ClearBtn>
            )}
          </DropdownHeader>

          <NotifList>
            {notifications.length > 0 ? (
              notifications.map((n) => (
                <NotifItem
                  key={n._id}
                  isDark={isDarkMode}
                  onClick={() => handleNotificationClick(n)}
                >
                  <NotifIcon>{typeIcon[n.type] || '🔔'}</NotifIcon>
                  <NotifBody>
                    <NotifText isDark={isDarkMode}>
                      <strong>{n.username}</strong> {typeLabel[n.type] || 'interactuó con'} el contenido de <strong>{n.contentOwner || 'otro usuario'}</strong>
                    </NotifText>
                    <NotifTitle isDark={isDarkMode}>{n.contentTitle}</NotifTitle>
                    <NotifTime isDark={isDarkMode}>
                      {new Date(n.createdAt).toLocaleDateString('es-ES', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      })}
                    </NotifTime>
                  </NotifBody>
                </NotifItem>
              ))
            ) : (
              <Empty isDark={isDarkMode}>
                <span style={{ fontSize: '32px' }}>🔔</span>
                No hay notificaciones nuevas
              </Empty>
            )}
          </NotifList>
        </Dropdown>
      )}
    </Container>
  );
};

/* ── STYLED COMPONENTS ── */

const shake = keyframes`
  0%,100% { transform: rotate(0deg); }
  20%      { transform: rotate(-15deg); }
  40%      { transform: rotate(15deg); }
  60%      { transform: rotate(-10deg); }
  80%      { transform: rotate(10deg); }
`;

const Container = styled.div`
  position: relative;
  margin-right: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const BellIcon = styled.img`
  width: 25px;
  height: 25px;
  transition: filter 0.3s ease;
  animation: ${props => props.hasNew ? shake : 'none'} 0.6s ease;
  [data-theme='light'] & { filter: invert(1) contrast(2); }
  [data-theme='dark'] &  { filter: none; }
`;

const Badge = styled.div`
  position: absolute;
  top: -8px;
  right: -4px;
  background: #ff4d4d;
  color: white;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  z-index: 1;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 38px;
  right: 0;
  width: 300px;
  background: ${p => p.isDark ? '#1e1e1e' : '#fff'};
  border: 1px solid ${p => p.isDark ? '#333' : '#e0e0e0'};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
  z-index: 2000;
  overflow: hidden;

  @media (max-width: 768px) {
    position: fixed;
    top: 65px;
    left: 50%;
    transform: translateX(-50%);
    width: 90vw;
    max-width: 360px;
    right: auto;
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${p => p.isDark ? '#333' : '#eee'};
`;

const HeaderTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.isDark ? '#fff' : '#111'};
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: #a855f7;
  cursor: pointer;
  font-weight: 600;
  &:hover { opacity: 0.7; }
`;

const NotifList = styled.div`
  max-height: 360px;
  overflow-y: auto;
`;

const NotifItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid ${p => p.isDark ? '#2a2a2a' : '#f5f5f5'};
  &:hover { background: ${p => p.isDark ? '#2a2a2a' : '#f9f5ff'}; }
  &:last-child { border-bottom: none; }
`;

const NotifIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const NotifBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const NotifText = styled.div`
  font-size: 13px;
  color: ${p => p.isDark ? '#ddd' : '#333'};
  line-height: 1.4;
`;

const NotifTitle = styled.div`
  font-size: 12px;
  color: #a855f7;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotifTime = styled.div`
  font-size: 11px;
  color: ${p => p.isDark ? '#666' : '#aaa'};
  margin-top: 2px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 30px 20px;
  font-size: 13px;
  color: ${p => p.isDark ? '#555' : '#bbb'};
`;

export default NotificationBell;